/* ==========================================================================
   PRC interactive-guide system: shared behaviour (guide.js)
   Loaded as <script type="module" defer> ONLY on the guide template.
   Autonomous custom elements. Progressive enhancement: the authored body emits
   TAGS only; behaviour lives here and is reused across guides + clients. Every
   element degrades to a readable static document if this file never runs.
   Idempotent; respects prefers-reduced-motion.
   KEEP THIS FILE import/export-FREE. It is loaded as a classic script in the
   local preview shell (guide-starter.html) over file://, where ES modules are
   blocked. No top-level import/export, so both module and classic modes match.
   ========================================================================== */

/* --- <guide-accordion [single]> -------------------------------------------
   Wraps native <details>. Works with zero JS. With the `single` attribute it
   behaves as an exclusive accordion (one open at a time), scoped to its own
   top-level rows so a nested <details> never closes its parent. -------------- */
class GuideAccordion extends HTMLElement {
  connectedCallback() {
    if (this.__wired || !this.hasAttribute('single')) return;
    this.__wired = true;
    this.addEventListener('toggle', (e) => {
      const d = e.target;
      if (d.parentElement === this && d.open) {
        this.querySelectorAll(':scope > details[open]').forEach((o) => { if (o !== d) o.open = false; });
      }
    }, true);
  }
}

/* --- <guide-tabs> ---------------------------------------------------------
   Kept for future use but NOT in the authoring recipe or the validator yet, so
   authors cannot ship an untaught, mis-wired variant. Enable via a short dev
   step (document the markup + add to ALLOWED_TAGS) when a guide needs tabs. --- */
class GuideTabs extends HTMLElement {
  connectedCallback() {
    if (this.__wired) return;
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = this.tabs.map((t) => { const id = t.getAttribute('aria-controls'); return id ? this.querySelector('#' + CSS.escape(id)) : null; });
    if (!this.tabs.length || this.panels.some((p) => !p)) return;
    this.__wired = true;
    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (e) => this.onKey(e, i));
    });
    this.select(Math.max(0, this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true')));
  }
  select(i) {
    this.tabs.forEach((tab, j) => {
      const on = i === j;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
      this.panels[j].hidden = !on;
    });
  }
  onKey(e, i) {
    const last = this.tabs.length - 1;
    let n = null;
    if (e.key === 'ArrowRight') n = i === last ? 0 : i + 1;
    else if (e.key === 'ArrowLeft') n = i === 0 ? last : i - 1;
    else if (e.key === 'Home') n = 0;
    else if (e.key === 'End') n = last;
    if (n !== null) { e.preventDefault(); this.select(n); this.tabs[n].focus(); }
  }
}

/* --- <guide-calc>: pond fountain sizing -----------------------------------
   Renders its inputs server-static (in the body HTML). Reads pond dimensions,
   returns surface acres, volume in gallons, and a starting horsepower using the
   1 to 2 HP per surface acre rule, then offers a shop CTA. Honest + hedged. */
const HP_STEPS = [1 / 3, 1 / 2, 3 / 4, 1, 1.5, 2, 3, 5, 7.5, 10, 15, 20, 30];
const snapHp = (v) => {
  for (const s of HP_STEPS) if (s >= v - 1e-9) return s;
  return HP_STEPS[HP_STEPS.length - 1];
};
const hpLabel = (v) => (v === 1 / 3 ? '1/3' : v === 1 / 2 ? '1/2' : v === 3 / 4 ? '3/4' : String(v));
// Only allow safe link schemes for the CTA (data-cta-url is author-supplied).
const safeUrl = (u) => { u = String(u || '').trim(); return (/^(https?:|mailto:|tel:)/i.test(u) || /^[/#]/.test(u)) ? u : '#'; };

class GuideCalc extends HTMLElement {
  connectedCallback() {
    if (this.__wired) return;
    this.__wired = true;
    this.q = (r) => this.querySelector('[data-role="' + r + '"]');
    this.out = this.q('out');
    if (!this.out) return;
    ['shape', 'len', 'wid', 'dep'].forEach((r) => {
      const el = this.q(r);
      if (el) el.addEventListener('input', () => this.update());
    });
    const shape = this.q('shape');
    if (shape) shape.addEventListener('change', () => this.update());
  }
  num(r) { const el = this.q(r); const v = el ? parseFloat(el.value) : NaN; return isFinite(v) && v > 0 ? v : 0; }
  update() {
    cancelAnimationFrame(this._raf);
    const circ = this.q('shape') && this.q('shape').value === 'circ';
    const len = this.num('len'), wid = this.num('wid'), dep = this.num('dep');
    // Ellipse area for a round/oval pond (a true circle is just len === wid); box area for rectangular.
    const sqft = circ ? Math.PI * (len / 2) * (wid / 2) : len * wid;
    if (!sqft) { this.out.hidden = true; this.out.textContent = ''; return; }
    const acres = sqft / 43560;
    const low = snapHp(Math.max(1 / 3, acres));      // ~1 HP per acre floor
    const high = snapHp(Math.max(low, acres * 2));   // up to 2 HP per acre
    const gallons = dep ? Math.round(sqft * dep * 7.48) : 0;   // true area x depth x gal/ft3
    const url = safeUrl(this.getAttribute('data-cta-url') || '/collections/pond-fountains');
    const label = this.getAttribute('data-cta-label') || 'Shop pond fountains';
    const acresTxt = acres < 0.1 ? acres.toFixed(3) : acres.toFixed(2);
    const hpTxt = hpLabel(low) === hpLabel(high) ? hpLabel(low) + ' HP' : hpLabel(low) + ' to ' + hpLabel(high) + ' HP';
    const noteTxt = 'Your pond is about ' + acresTxt + ' surface acres' +
      (gallons ? ' and roughly ' + gallons.toLocaleString() + ' gallons' : '') +
      '. This uses the 1 to 2 HP per surface acre rule. Confirm it against your depth and shape. Anything below the 6 foot mixing limit may also need bottom aeration.';
    // Reveal the live region first, then build it on the next frame so the aria-live
    // announcement fires reliably. Build via DOM nodes (textContent) so author-supplied
    // CTA label/url can never inject markup.
    this.out.hidden = false;
    this._raf = requestAnimationFrame(() => {
      this.out.textContent = '';
      const r = document.createElement('p'); r.className = 'guide__calc-result'; r.textContent = 'Starting size: about ' + hpTxt;
      const n = document.createElement('p'); n.className = 'guide__calc-note'; n.textContent = noteTxt;
      const a = document.createElement('a'); a.className = 'guide__btn guide__calc-cta'; a.textContent = label; a.href = url;
      this.out.append(r, n, a);
    });
  }
}

/* --- TOC auto-build + scroll-spy (desktop enhancement only) ----------------
   Assign ids to the body's H2s and fill the [data-guide-toc] nav (which the
   section ships `hidden`, so no-JS = no orphan title). Runs only at desktop
   width because the TOC is display:none on mobile. -------------------------- */
function slugify(s) { return String(s).toLowerCase().replace(/[^\w]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60); }
function buildToc(root) {
  const body = root.querySelector('.guide__body');
  const toc = root.querySelector('[data-guide-toc]');
  if (!body || !toc) return;
  const heads = Array.from(body.querySelectorAll('h2'));
  const used = new Set();
  const list = toc.querySelector('ol') || toc.appendChild(document.createElement('ol'));
  list.textContent = '';
  let count = 0;
  heads.forEach((h, i) => {
    let base = h.id || slugify(h.textContent || '') || ('section-' + (i + 1));
    let id = base, n = 1;
    while (used.has(id)) id = base + '-' + (++n);
    used.add(id);
    h.id = id;
    const li = document.createElement('li');
    // A <button>, not an <a href="#id">: no fragment href ever lands in the DOM,
    // so Google's renderer has no hash link to discover, and clicking never touches
    // the URL bar. Native browser deep-links to #id on load still work, the ids stay.
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'guide__toc-link';
    btn.dataset.goto = id;
    btn.textContent = h.textContent;   // DOM assignment escapes automatically
    li.appendChild(btn);
    list.appendChild(li);
    count++;
  });
  // Only reveal the sidebar once it actually holds a useful list.
  toc.hidden = count < 3;
}

function initTocSpy(root) {
  const toc = root.querySelector('.guide__toc');
  if (!toc || toc.hidden || !('IntersectionObserver' in window)) return;
  if (root.__tocIO) root.__tocIO.disconnect();   // no observer leak on re-boot
  const links = new Map();
  toc.querySelectorAll('button[data-goto]').forEach((b) => links.set(b.dataset.goto, b));
  if (!links.size) return;
  let current = null;
  const setActive = (id) => {
    if (id === current) return;
    if (current && links.get(current)) links.get(current).classList.remove('is-active');
    if (links.get(id)) links.get(id).classList.add('is-active');
    current = id;
  };
  const io = new IntersectionObserver((entries) => {
    const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
    if (visible.length) setActive(visible[0].target.id);
  }, { rootMargin: '-84px 0px -70% 0px', threshold: 0 });
  root.__tocIO = io;
  links.forEach((_, id) => { const el = document.getElementById(id); if (el) io.observe(el); });
}

/* --- TOC click: scroll only, never touches the URL or history -------------- */
if (!window.__guideTocClickBound) {
  window.__guideTocClickBound = true;
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.guide__toc-link');
    if (!btn) return;
    const target = document.getElementById(btn.dataset.goto);
    if (!target) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    // Move focus to the landed section, same as a native hash jump would, so
    // keyboard and screen-reader users get the same result without an href.
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
  });
}

/* --- define (idempotent) --------------------------------------------------- */
function define(name, cls) { if (!customElements.get(name)) customElements.define(name, cls); }
define('guide-accordion', GuideAccordion);
define('guide-tabs', GuideTabs);
define('guide-calc', GuideCalc);

function initGuide(root) {
  const mq = window.matchMedia('(min-width: 60rem)');
  const run = () => { buildToc(root); initTocSpy(root); };
  if (mq.matches) run();                       // build only where the TOC is visible
  if (!root.__mqBound) { root.__mqBound = true; mq.addEventListener('change', (e) => { if (e.matches) run(); }); }
}
const boot = () => { document.querySelectorAll('.guide').forEach(initGuide); };
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
// Theme editor: the section HTML is swapped on edit, so rebuild the TOC/scroll-spy.
document.addEventListener('shopify:section:load', boot);
